import { Button } from '@/components/DesignSystem/Atomics';
import { useGlobalProps } from '@/components/GlobalContextProvider';
import useViewEvents from '@/hooks/useViewEvent';
import { MobiusTrackingContext } from '@/types/Mobius';
import getAliasParams from '@/utils/getAliasParams';
import getTrackingContext from '@/utils/getTrackingContext';
import getTrafficSource from '@/utils/getTrafficSource';
import getUrlQuery from '@/utils/getUrlQuery';
import { newRelicNoticeError } from '@/utils/newRelicActions';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import React, { useEffect, useRef, useState } from 'react';

export type SchoolInfoCardProps = {
  program: {
    degreeLevel: string;
    name: string;
    description: string;
    image: string;
    ctaText: string;
    altText: string;
    url: string;
    schoolSlug: string;
    schoolName: string;
    id: string;
    capId?: string;
    requestId?: string;
    helid?: string;
    related?: boolean;
  };
  selectedCategory?: string;
  selectedSubject?: string;
  selectedDegree?: string;
  index?: number;
  viewCorrelationId?: string;
};

function SchoolInfoCard({
  program,
  selectedCategory,
  selectedDegree,
  selectedSubject,
  index,
  viewCorrelationId,
}: SchoolInfoCardProps) {
  const { siteConfig } = useGlobalProps();
  const [trackingContext, setTrackingContext] = useState<
    Partial<MobiusTrackingContext>
  >({ formatSubtype: 'salary-calculator-entry', formatType: 'tool' });

  const waitForTrackingContext = async () => {
    try {
      const trackingContextWaitResult =
        await getTrackingContext(trackingContext);
      setTrackingContext(trackingContextWaitResult);
    } catch (error) {
      // Handle any errors that occur during the asynchronous operation
      console.error('Error waiting for tracking context:', error);
      newRelicNoticeError(
        new Error(`Error waiting for tracking context: ${error}`),
      );
    }
  };

  // get tracking context
  useEffect(() => {
    waitForTrackingContext();
  }, []);

  const handleCardClick = () => {
    const position = (index !== undefined ? index + 1 : 0) || 0;
    const correlationId = uuid();

    const outboundUrl = getUrlQuery(program.url, {
      publisher: siteConfig?.domain || '',
      viewCorrelationId,
      idToken: siteConfig?.identityToken || '',
      helid: program?.helid || '',
      sku: program?.capId || '',
      listId: program?.requestId || '',
      position,
      trackingContext: JSON.stringify(trackingContext),
      source: getTrafficSource(window.location.href),
      degree: selectedDegree || '',
      category: selectedCategory || '',
      subject: selectedSubject || '',
      correlationId,
      ...getAliasParams(),
    });

    tagularEvent('ProductClicked', {
      correlationId,
      outboundUrl: outboundUrl,
      viewCorrelationId: viewCorrelationId,
      product: {
        brand: program.schoolName,
        category: selectedCategory,
        formatSubtype: 'salary-calculator-entry',
        formatType: 'tool',
        location: 'salary-calculator',
        name: selectedSubject,
        position: position,
        productId: program.id ? program.id.toString() : '',
        sku: program.capId ? program.capId.toString() : '',
        variant: selectedDegree,
      },
    });

    // Navigate to the URL
    window.open(outboundUrl, '_blank', 'noopener,noreferrer');
  };

  const position = (index !== undefined ? index + 1 : 0) || 0;

  const baseEventPayload = {
    product: {
      brand: program.schoolSlug,
      category: selectedCategory || 'general',
      formatSubtype: 'salary-calculator-entry',
      formatType: 'tool',
      location: 'salary-calculator',
      name: selectedSubject || 'general',
      position: position,
      productId: program.id ? program.id.toString() : '',
      sku: program.capId || '',
      variant: selectedDegree || 'general',
    },
    listId: program.requestId || '',
    viewCorrelationId,
  };

  // tagular - ProductViewed
  const { ref } = useViewEvents([
    {
      name: 'ProductViewed',
      payload: baseEventPayload,
    },
  ]);
  return (
    <div
      ref={ref as React.LegacyRef<HTMLDivElement>}
      className='not-prose bg-white rounded-lg shadow-md overflow-hidden flex flex-col hover:cursor-pointer'
      onClick={handleCardClick}
    >
      {/* Primary accent bar */}
      <div className='h-2 bg-primary-500'></div>

      <div className='p-4 flex-grow'>
        {/* Program type */}
        <p className='text-gray-600 mb-1 font-body-12 md:font-body-14'>
          {program.schoolName}
        </p>

        {/* name */}
        <div className='font-body-16 line-clamp-3 md:font-body-18 font-bold mb-3'>
          {program.name}
        </div>

        {/* Program description */}
        <p className='text-gray-700 line-clamp-3 overflow-hidden font-body-12 md:font-body-14'>
          {program.description}
        </p>
      </div>

      {/* Footer with school image and learn more button */}
      <div className='px-4 pb-4 flex items-start justify-between mt-auto gap-4'>
        <div className='border border-gray-300 p-2 rounded w-1/2 flex justify-center items-center'>
          <img
            src={`https://simple-storage-server.highereducation.com/${program.schoolSlug}.png?w=160&h=72`}
            alt={`${program.name} Logo`}
            style={{ maxHeight: '72px' }}
            className='max-w-full h-auto object-contain'
          />
        </div>
        <div className='w-1/2'>
          <Button
            variant='filled'
            size='xsmall'
            className='flex items-center rounded-small w-full justify-center'
            link={program.url}
            label={program.ctaText}
            onClick={handleCardClick}
          />
        </div>
      </div>
    </div>
  );
}

export default SchoolInfoCard;
